import React from "react";
import {useTranslation} from "react-i18next";
import cx from "classnames";

import Typography from "@mui/material/Typography";

import { ReactComponent as PrintBrand } from 'assets/brand/PrintBrand.svg';
import {COLORS} from "../../../../../../../../../../../../../config/constants";
import {useProductsList} from "../../../../../../../../../../../../../api/calc";

import {getColumnName} from "../../constants";

import './print_table.scss';
import {numberWithSpaces} from "../../../../../../../../../../../../../utils/helpers";

type Props = {
  isHorizontal: boolean;
  title: string;
  currentProducts?: string[]
  activeColumns: string[]
  setActiveColumns(id: any): void
}

const PrintTable = React.forwardRef<any, Props>((props, ref) => {

  const { t } = useTranslation()

  const productsList = useProductsList()?.data;

  const hasVolumeCartoon = productsList?.results?.some(product => product.volume_cartoon > 0);
  const hasVolumeManual = productsList?.results?.some(product => product.volume_manual > 0);
  const hasNetWeightManual = productsList?.results?.some(product => product.net_weight_manual > 0);
  const hasGrossWeightManual = productsList?.results?.some(product => product.gross_weight_manual > 0);
  const hasPalleteCnt = productsList?.results?.some(product => product.pallete_cnt > 0);
  const hasPalleteType = productsList?.results?.some(product => product.pallete_type !== null);

  const getHeadInfo = () => {

    const allColumns = [
      {
        id: 'id',
        label: getColumnName('id', t),
      },
      {
        id: 'name',
        label: getColumnName('name', t),
      },
      {
        id: 'pieces',
        label: getColumnName('pieces', t),
      },
      {
        id: 'places_manual',
        label: getColumnName('places_manual', t),
      },
      {
        id: 'pieces_in_cartoon',
        label: getColumnName('pieces_in_cartoon', t),
      },
      {
        id: 'unit_price',
        label: getColumnName('unit_price', t),
      },
      {
        id: 'price',
        label: getColumnName('price', t),
      },
      ...(hasVolumeCartoon
        ? [
          {
            id: 'volume_cartoon',
            label: getColumnName('volume_cartoon', t),
          }
        ]
        : []),
      ...(hasVolumeManual
        ? [
          {
            id: 'volume_manual',
            label: getColumnName('volume_manual', t),
          }
        ]
        : []),
      ...(hasNetWeightManual
        ? [
          {
            id: 'net_weight_manual',
            label: getColumnName('net_weight_manual', t),
          }
        ]
        : []),
      ...(hasGrossWeightManual
        ? [
          {
            id: 'gross_weight_manual',
            label: getColumnName('gross_weight_manual', t),
          }
        ]
        : []),
      {
        id: 'volume_total',
        label: getColumnName('volume_total', t),
      },
      {
        id: 'net_weight_cartoon',
        label: getColumnName('net_weight_cartoon', t),
      },
      {
        id: 'gross_weight_cartoon',
        label: getColumnName('gross_weight_cartoon', t),
      },
      {
        id: 'net_weight_total',
        label: getColumnName('net_weight_total', t),
      },
      {
        id: 'gross_weight_total',
        label: getColumnName('gross_weight_total', t),
      },
      ...(hasPalleteCnt
        ? [
          {
            id: 'pallete_cnt',
            label: getColumnName('pallete_cnt', t),
          }
        ]
        : []),
      ...(hasPalleteType
        ? [
          {
            id: 'pallete_type',
            label: getColumnName('pallete_type', t),
          }
        ]
        : []),
    ];

    return allColumns.filter((column) => props.activeColumns.includes(column.id));
  }

  const rows = productsList?.results.map((productListItem: ProductListItem, index: number) => ({
    ...productListItem,
    id: index + 1,
  }));

  const columnsToShow = getHeadInfo();

  const renderCellValue = (columnId: string, value?: number | string) => {
    if (columnId === 'name') {
      return value;
    }

    return numberWithSpaces(value);
  }

  const renderRow = (row: ProductListItem & { id: number }) => {
    return <tr key={row.id}>
      {columnsToShow.map((columnName) => (
        <td key={columnName.id}>
          {renderCellValue(columnName.id, (row as any)[columnName.id])}
        </td>
      ))}
    </tr>
  }

  const renderTotal = () => columnsToShow.map((columnName, index) => {
    if (index === 0) {
      return (
        <td key={columnName.id} className='product-table-print__total' colSpan={2}>{t('common__text__inTotal')}</td>
      );
    }
    if (index === 1) {
      return null;
    }

    const normalizedId = columnName.id.replace(/_total$/, '');
    // @ts-ignore
    const valueToDisplay = numberWithSpaces(productsList?.summary?.[`total_${normalizedId}`]);

    return <td key={columnName.id} className='product-table-print__totalPrice'>{valueToDisplay}</td>;
  });

  return (
    // @ts-ignore
    <div ref={ref} className={cx('product-table-print__container', {
      'product-table-print__container_isHorizontal': props.isHorizontal,
    })}>
      <PrintBrand className='product-table-print__image'/>
      <hr className='product-table-print__hr' />
      <Typography variant='caption' sx={{
        marginTop: '22px',
        color: COLORS.BLACK_DARK,
        marginBottom: '6px',
        fontWeight: 500,
        display: 'block',
        letterSpacing: 0,
      }}>
        {props.title}
      </Typography>
      <table className={cx('product-table-print', {
        'product-table-print_isHorizontal': props.isHorizontal,
      })}>
        <thead>
          <tr className='product-table-print__head'>
            {columnsToShow.map(item => <th scope='col' key={item.id}>{item.label}</th>)}
          </tr>
        </thead>
        <tbody className='product-table-print__body'>
          {/* @ts-ignore */}
          {rows.map(renderRow)}
          <tr>
            <td colSpan={columnsToShow.length} className='product-table-print__empty'/>
          </tr>
          <tr>
            {renderTotal()}
          </tr>
        </tbody>
      </table>
    </div>
  )
})

export default PrintTable
